import { AuthResponse, LoginRequest, StorageData, ChangePasswordRequest } from '../types/auth.types';
import { StorageService } from './storage.service';
import { API_URL } from '../../common/utils/util';

export class AuthService {
  private static refreshPromise: Promise<AuthResponse> | null = null;
  static async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Login failed');
      }

      const data: AuthResponse = await response.json();
      const expiresIn = Date.now() + (data.expiresIn * 1000);
      
      const storageData: StorageData = {
        ...data,
        expiresIn,
      };

      StorageService.saveAuthData(storageData);
      const responseWithCalculatedExpiry: AuthResponse = {
        ...data,
        expiresIn,
      };
      
      return responseWithCalculatedExpiry;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  static async refreshToken(): Promise<AuthResponse> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this._performRefreshToken();
    
    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  private static async _performRefreshToken(): Promise<AuthResponse> {
    try {
      const refreshToken = StorageService.getRefreshToken();
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshToken,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Token refresh failed');
      }

      const data: AuthResponse = await response.json();
      
      const expiresIn = Date.now() + (data.expiresIn * 1000);
      
      const storageData: StorageData = {
        ...data,
        expiresIn,
      };

      StorageService.saveAuthData(storageData);
      return data;
    } catch (error) {
      console.error('Refresh token error:', error);
      StorageService.clearAuthData();
      throw error;
    }
  }

  static async validateAuth(): Promise<boolean> {
    try {
      const isExpired = StorageService.isTokenExpired();
      
      if (isExpired) {
        await this.refreshToken();
      }
      
      return true;
    } catch (error) {
      console.error('Auth validation error:', error);
      return false;
    }
  }

  static logout(): void {
    StorageService.clearAuthData();
  }

  static isAuthenticated(): boolean {
    const accessToken = StorageService.getAccessToken();
    const refreshToken = StorageService.getRefreshToken();
    
    if (!accessToken || !refreshToken) {
      return false;
    }
    return true;
  }

  static async validateAndRefreshAuth(): Promise<boolean> {
    try {
      const isExpired = StorageService.isTokenExpired();
      
      if (isExpired) {
        try {
          await this.refreshToken();
          return true;
        } catch (refreshError) {
          console.error('Token refresh failed, logging out:', refreshError);
          this.logout();
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Auth validation error:', error);
      this.logout();
      return false;
    }
  }

  /**
   * Cambia la contraseña del usuario autenticado
   * @param passwordData - Datos para el cambio de contraseña
   * @returns Promise que resuelve cuando la contraseña se ha cambiado exitosamente
   * @throws Error si el cambio de contraseña falla
   */
  static async changePassword(passwordData: ChangePasswordRequest): Promise<void> {
    try {
      const { ApiClient } = await import('../../common/services/apiClient');
      await ApiClient.post('/auth/change-password', passwordData);
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  }

  /**
   * Maneja errores de API y determina si se requiere cambio de contraseña
   * @param error - Error de la respuesta de la API
   * @returns true si se requiere cambio de contraseña, false en caso contrario
   */
  static isPasswordChangeRequired(error: any): boolean {
    if (error?.statusCode !== 403) return false;
    
    return (
      error?.code === 'PASSWORD_CHANGE_REQUIRED' ||
      error?.error === 'PASSWORD_CHANGE_REQUIRED' ||
      error?.requiresPasswordChange === true
    );
  }

  /**
   * Verifica si el token actual está próximo a expirar
   * @param thresholdMinutes - Minutos antes de la expiración para considerarlo próximo (default: 5)
   * @returns true si el token expira pronto, false en caso contrario
   */
  static isTokenExpiringSoon(thresholdMinutes: number = 5): boolean {
    const expiresIn = StorageService.getExpiresIn();
    if (!expiresIn) return true;
    
    const currentTime = Date.now();
    const thresholdTime = currentTime + (thresholdMinutes * 60 * 1000);
    
    return expiresIn <= thresholdTime;
  }

} 